import { useContext, useEffect, useState } from "react";
import { Button, FormControl, MenuItem, Pagination, Select, SelectChangeEvent } from "@mui/material";
import { Modal } from "../Modal/Modal";
import {
  DataGrid,
  Column,
  FilterRow,
  HeaderFilter,
  LoadPanel,
  Scrolling,
  SearchPanel,
  Selection,
  Toolbar,
  Item,
  Paging,
} from "devextreme-react/data-grid";
import { useRecoilValue } from "recoil";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { v4 as uuidv4 } from "uuid";

import { taxPortalClientApi, userAssignmentClientApi, createSitesClientApi, corporateSightClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { updateSites } from "../../../pages/UpdateScreen/UpdateSiteInfo.types";
import { ViewExecutionLogsProps } from "./ViewExecutionLogs.types";
import { dataGridConfig, defaultPageSize, SCHEDULE_LIMIT_API_CALLS, HQISheetName } from "../../../constants";
import { convertUTCTimeToLocal, calculateGridHeight } from "../../../util/Utils";
import useSnackbar from "../../../hooks/useSnackbar";
import { SnackbarMessage } from "../../../hooks/useSnackbar.types";
import { getAppName } from "../../commonComponents/AppNameToggle";
import {
  GetAllSitesDTO,
  ServiceBusEnum,
  ServiceMessage,
  SyncTypeEnum,
  SelectName,
  GetSchedulesDTO,
} from "../../../api-client-nswag/taxportal-client";
import { UserAssignTpac, TpacRequestBasicMessage } from "../../../api-client-nswag/userassignment-client";
import { OidcIdentityContext } from "@rsmus/react-auth/dist/OidcIdentity";
import { DialogBox } from "../../../pages/CreateSites/DialogBox";
import { displayNameToSyncTypeMap } from "../../../pages/scheduleFiles/ScheduleFiles";

const initialStateData = { pending: false, fulfilled: false, sites: [], totalCount: 0 };

const siteMetricsDisplayNameToISheetName: Record<string, string> = {
  "(MS) Site Metrics IR Record Count": HQISheetName.INFORMATION_REQUEST,
  "(MS) Site Metrics PS Record Count": HQISheetName.PROJECT_STATUS,
  "(MS) Site Metrics Controversy Tracker Record Count": HQISheetName.CONTROVERSY_TRACKER,
  "(MS) Site Metrics K-1 Tracker Record Count": HQISheetName.K1_TRACKER,
  "(MS) Site Metrics New Opportunities Record Count": HQISheetName.NEW_OPPORTUNITIES_CHECKLIST,
  "(MS) Site Metrics Cash Tax Payments Record Count": HQISheetName.CASH_TAX_PAYMENTS,
  "(MS) Site Metrics Issues Tracker Record Count": HQISheetName.ISSUES_TRACKER,
  "(MS) Site Metrics Estimates Tracker Record Count": HQISheetName.ESTIMATES_TRACKER,
  "(MS) Site Metrics Notice Tracker Record Count": HQISheetName.NOTICE_TRACKER,
  "(MS) Site Metrics Deliverable Listing Record Count": HQISheetName.DELIVERABLE_LISTING,
  "(MS) Site Metrics Engagement Overview Record Count": HQISheetName.ENGAGEMENT_OVERVIEW,
  "(MS) Site Metrics Deliverables Record Count": HQISheetName.DELIVERABLES,
};

export const ViewExecutionLogs = ({
  optionsDropdown,
  optionSelected,
  isDisplayModal,
  handleClose,
}: ViewExecutionLogsProps) => {
  const { user, logout } = useContext(OidcIdentityContext);
  const { showSnackbar } = useSnackbar();
  const { showSuccessSnackbar } = useSnackbar();
  const [data, setData] = useState<updateSites>(initialStateData);
  const [dropDown, setDropDown] = useState<number>();
  const [selectedTypeNumber, setSelectedTypeNumber] = useState<number>();
  const [pageNumber, setpageNumber] = useState(1);
  const appName = useRecoilValue(getAppName);
  const [selectedData, setSelectedData] = useState([]);
  const dropdownOptions = optionsDropdown ?? [];
  const [disableRetry, setDisableRetry] = useState<boolean>(true);
  const [showAlert, setShowAlert] = useState(false);
  const [checkBoxesMode, setCheckBoxesMode] = useState<string>("");
  const { INFORMATION_REQUEST, PROJECT_STATUS, DOCUMENT_METADATA, LEGAL_ENTITIES, SYNC_MDM, THIRD_PARTY_ORG } =
    SelectName;

  useEffect(() => {
    if (optionSelected == null) return;
    const selectedOption = options.find(
      (option) => option.value === optionSelected
    );
    setDropDown(optionSelected);
    setSelectedTypeNumber(selectedOption?.type);
  }, [optionSelected]);

  useEffect(() => {
    if (!!selectedTypeNumber) fetchDetails(selectedTypeNumber);
  }, [selectedTypeNumber, pageNumber]);

  useEffect(() => {
    if (isDisplayModal) {
      setpageNumber(1);
    }
  }, [isDisplayModal]);

  const options = dropdownOptions.map((option) => ({
    name: option.displayName,
    value: option.id,
    appName: option.appName,
    type:
      displayNameToSyncTypeMap[option.displayName] ??
      SyncTypeEnum[option.jobType as keyof typeof SyncTypeEnum],
  }));

  const filteredOptions = options.filter((option) =>
    appName === "CS" ? option.appName === "CS" : option.appName !== "CS"
  );
  const fetchDetails = async (dropDownValue: number | null) => {
    setData({ pending: true, fulfilled: true, sites: [], totalCount: 0 });
    try {
      const startIndex = pageNumber === 1 ? 0 : (pageNumber - 1) * defaultPageSize;
      if (appName === "TP,CS") {
        const [tpData, csData] = await Promise.all([
          createSitesClientApi.taxPortal_GetAllSites(
            dropDownValue!,
            null, //Username.ADMIN,
            "TP",
            startIndex,
            defaultPageSize
          ),
          createSitesClientApi.taxPortal_GetAllSites(
            dropDownValue!,
            null, //Username.ADMIN,
            "CS",
            startIndex,
            defaultPageSize
          )
        ]);

        // Combine the data from both TP and CS
        const combinedSites = [...(tpData.data || []), ...(csData.data || [])]
          .sort((a, b) => {
            const dateA = new Date(a.siteCreated || 0).getTime();
            const dateB = new Date(b.siteCreated || 0).getTime();
            return dateB - dateA;
          });
        const combinedTotalCount = (tpData.totalCount || 0) + (csData.totalCount || 0);

        setData({
          pending: false,
          fulfilled: true,
          sites: combinedSites,
          totalCount: combinedTotalCount
        });
      } else {
        const data = await createSitesClientApi.taxPortal_GetAllSites(
          dropDownValue!,
          null, //Username.ADMIN,
          appName,
          startIndex,
          defaultPageSize
        );
        setData({ pending: false, fulfilled: true, sites: data.data, totalCount: data.totalCount });
      }

      showSnackbar({ message: SnackbarMessage.SCHEDULE_DETAILS });
    } catch (error) {
      setData(initialStateData);
    }
  };
  const handleRefresh = () => fetchDetails(selectedTypeNumber!);

  const refreshButtonOptions = {
    icon: "refresh",
    onClick: handleRefresh,
  };

  const cellRenderDate = (e: string) => {
    return <div>{convertUTCTimeToLocal(e)}</div>;
  };
  const cellRenderForMessage = (e: any) => {
    if (e.data.message === null && e.data.status === null) {
      return null;
    }
    return <div title={e.data.message}>{e.data.status}</div>;
  };

  const handleDropDownChange = (e: SelectChangeEvent<number>) => {
    setDropDown(e.target.value as number);
    setSelectedTypeNumber(options.find((option) => option.value === e.target.value)?.type);
  };

  const onSelectionchanged = (e: any) => {
    const selectedRows = e.selectedRowsData;

    if (selectedRows.length > SCHEDULE_LIMIT_API_CALLS) {
      const lastSelectedKey = e.currentSelectedRowKeys?.[0];
      if (lastSelectedKey) {
        e.component.deselectRows([lastSelectedKey]);
      }
      setShowAlert(true);
      return;
    }

    setShowAlert(false);
    setSelectedData(selectedRows);
    // const hasError = selected.some((row: any) => row.status === "ERROR" || row.status == null);
    // setDisableRetry(hasError ? false : true);

    // For 6.2 release: always disable Retry for MS Rollover Retrieval Storage Cleanup jobs
    if (options.find((option) => option.type === selectedTypeNumber)?.name === "(MS) Rollover Retrival Storage Cleanup") {
      setDisableRetry(true);
    } else {
      setDisableRetry(selectedRows.length === 0);
    }
  };

  const exportButtonOptions = {
    icon: "exportxlsx",
    onClick: async () => {
      try {
        if (appName === "TP,CS") {
          const [tpResponse, csResponse] = await Promise.all([
            taxPortalClientApi.taxPortal_ExportToExcelForAllSites(
              selectedTypeNumber!,
              null, //Username.ADMIN,
              "TP"
            ),
            taxPortalClientApi.taxPortal_ExportToExcelForAllSites(
              selectedTypeNumber!,
              null, //Username.ADMIN,
              "CS"
            )
          ]);
          const url = URL.createObjectURL(new Blob([tpResponse.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "TP-CS-" + selectedTypeNumber! + " Execution Logs.xlsx");
          link.click();
          const csUrl = URL.createObjectURL(new Blob([csResponse.data]));
          const csLink = document.createElement("a");
          csLink.href = csUrl;
          csLink.setAttribute("download", "CS-" + selectedTypeNumber! + " Execution Logs.xlsx");
          csLink.click();
        } else {
          var response = await taxPortalClientApi
            .taxPortal_ExportToExcelForAllSites(
              selectedTypeNumber!,
              null, //Username.ADMIN,
              appName
            )
            .then((res: any) => {
              const url = URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", appName + "-" + selectedTypeNumber! + " Execution Logs.xlsx");
              link.click();
            });
        }

        showSuccessSnackbar({
          message: SnackbarMessage.EXPORT_TO_EXCEL_SUCCESS,
        });
      } catch (error) {
        showSnackbar({
          message: SnackbarMessage.EXPORT_TO_EXCEL_FAIL,
          severity: "error",
        });
      }
    },
  };

  const getEnumKeyByValue = (value: number): string | undefined => {
    return Object.keys(SyncTypeEnum).find((key) => SyncTypeEnum[key as keyof typeof SyncTypeEnum] === value);
  };

  const onClickRetrySite = async () => {
    try {
      const selectedOption = optionsDropdown.find((option) => option.id === dropDown);
      const selectedDisplayName = selectedOption?.displayName;
      //Only call UserAssignTpacTrigger if the CURRENTLY SELECTED dropdown is CHECK_EXTERNAL_USER_STATUS
      showSnackbar({
        message: SnackbarMessage.SCHEDULE_PROGRESS,
        severity: "info",
      })
      if (selectedDisplayName === "(MS) Check External User Status") {
        const tpacMessages: TpacRequestBasicMessage[] = selectedData.map((e: GetAllSitesDTO) => ({
          hqSiteId: e.hqSiteId,
          appName: appName,
          source: "Tpac-Scheduler",
        }));
        await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(UserAssignTpac.TriggerCheckStatus, tpacMessages);
      } else if (selectedDisplayName === "(MS) Sync CEM") {
        await Promise.all(
          (selectedData as GetAllSitesDTO[]).map(site =>
            createSitesClientApi.cem_SyncCEMProcess(
              site?.hqSiteId,
              user?.profile.preferred_username!
            )
          )
        );
        showSnackbar({
          message: SnackbarMessage.SUCCESS,
          severity: "success",
        });
      } else if (selectedDisplayName === "(MS) Create Folder With Permissions & Metadata") {
        await Promise.all(
          (selectedData as GetAllSitesDTO[]).map(site =>
            createSitesClientApi.hqCreateFolder_CreateFolderWithPermissionsAndMetadata(
              site?.hqSiteId,
              user?.profile.preferred_username!
            )
          )
        );
        showSnackbar({
          message: SnackbarMessage.SUCCESS,
          severity: "success",
        });
      } else if (selectedDisplayName === "(MS) Sync MDM Project & Phase") {
        await Promise.all(
          (selectedData as GetAllSitesDTO[]).map(site =>
            createSitesClientApi.mdm_SyncMdmProjectsOrPhases(
              site?.hqSiteId,
              undefined,
              user?.profile.preferred_username!
            )
          )
        );
        showSnackbar({
          message: SnackbarMessage.SUCCESS,
          severity: "success",
        });
      } else if (selectedDisplayName === "(MS) Update Project Status ISheet") {
        await Promise.all(
          (selectedData as GetAllSitesDTO[]).map(site =>
            createSitesClientApi.hqProjectStatus_UpdateProjectStatus(
              site?.hqSiteId,
              user?.profile.preferred_username!
            )
          )
        );
        showSnackbar({
          message: SnackbarMessage.SUCCESS,
          severity: "success",
        });
      } else if (selectedDisplayName === "(MS) Push Org To ISheet") {
        await Promise.all(
          (selectedData as GetAllSitesDTO[]).map(site =>
            createSitesClientApi.thirdPartyOrganization_SyncPopulateThirdPartyOrganization(
              site?.hqSiteId,
              user?.profile.preferred_username!
            )
          )
        );
        showSnackbar({
          message: SnackbarMessage.SUCCESS,
          severity: "success",
        });
      } else if (selectedDisplayName && siteMetricsDisplayNameToISheetName[selectedDisplayName]) {
        const isheetName = siteMetricsDisplayNameToISheetName[selectedDisplayName];
        await Promise.all(
          (selectedData as GetAllSitesDTO[]).map((site) =>
            createSitesClientApi.isheetReport_UpdateISheetReportStats(
              site?.hqSiteId,
              isheetName,
              user?.profile.preferred_username!
            )
          )
        );
        showSnackbar({
          message: SnackbarMessage.SUCCESS,
          severity: "success",
        });
      } else if (selectedOption?.jobType === "CORPORATE_SIGHT_MICRO_SERVICE") {
        await Promise.all(
          (selectedData as GetAllSitesDTO[]).map((site) => {
            const payload = {
              siteId: site.hqSiteId,
              siteName: site.hqClientName,
              eventType: selectedOption?.typeNumber,
              matterNumber: site.mdmClientId,
              appName,
              initiatedBy: user?.profile.preferred_username,
            };

            return corporateSightClientApi.postRequest(
              selectedOption?.siteServiceApi!,
              payload
            );
          })
        );
      } else {
        const schedules: GetSchedulesDTO[] = await taxPortalClientApi.functionScheduler_GetListOfScheduleJobs();
        const filteredSchedules = schedules.filter((x) => x.id === dropDown);
        const currentSchedule = filteredSchedules[0];
        const eventTypeId = currentSchedule?.typeNumber;
        const serviceMessages: ServiceMessage[] = selectedData
          .map((e: GetAllSitesDTO) => {
            switch (eventTypeId) {
              case ServiceBusEnum.CREATE_EXTERNAL_USER_CS:
                return [
                  {
                    siteId: e.hqSiteId,
                    userName: user?.profile.preferred_username,
                    eventType: eventTypeId,
                    mdmClientId: e.mdmClientId,
                    isTriggerFromScheduler: true,
                    isheetType: "Client User Requests",
                  },
                ];
              case ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY:
                return [
                  {
                    siteId: e.hqSiteId,
                    userName: user?.profile.preferred_username,
                    eventType: eventTypeId,
                    mdmClientId: e.mdmClientId,
                    isTriggerFromScheduler: true,
                    isheetType: "Third-party User Request Form",
                  },
                ];
              case ServiceBusEnum.MEMBER_FIRM:
                return [
                  {
                    siteId: e.hqSiteId,
                    userName: user?.profile.preferred_username,
                    eventType: eventTypeId,
                    mdmClientId: e.mdmClientId,
                    isTriggerFromScheduler: true,
                    isheetType: "Member Firm User Request Form",
                  },
                ];
              case ServiceBusEnum.CREATE_EXTERNAL_USER:
                return [
                  {
                    siteId: e.hqSiteId,
                    userName: user?.profile.preferred_username,
                    eventType: eventTypeId,
                    mdmClientId: e.mdmClientId,
                    isTriggerFromScheduler: true,
                    isheetType: "Client User Requests",
                  },
                ];
              case ServiceBusEnum.ROLLOVER_MICRO_SERVICE:
                return [
                  {
                    eventType: eventTypeId,
                    userName: user?.profile.preferred_username,
                    correlationId: uuidv4(),
                    isTriggerFromScheduler: true,
                  }
                ]
              default:
                return [
                  {
                    siteId: e.hqSiteId,
                    userName: user?.profile.preferred_username,
                    eventType: eventTypeId,
                    mdmClientId: e.mdmClientId,
                  },
                ];
            }
          })
          .flat();
        await taxPortalClientApi.taxPortal_InsertTopic(serviceMessages);
      }
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const getLimitMessage = (message: string) => {
    return message.replace("{xx}", SCHEDULE_LIMIT_API_CALLS.toString());
  };

  return (
    <Modal
      title="Schedule Execution logs"
      maxWidth="xl"
      btnCloseText="Cancel"
      isDisplayModal={isDisplayModal}
      handleClose={handleClose}
    >
      <div style={{ marginLeft: "20px", marginRight: "20px" }}>
        <DataGrid
          height={calculateGridHeight(270)}
          {...dataGridConfig}
          dataSource={data.sites}
          onSelectionChanged={onSelectionchanged}
          noDataText={data.pending ? "Loading..." : "No data"}
          onCellPrepared={(e) => {
            if (e.rowType === "data" && e.columnIndex === 0 && e.cellElement) {
              const disableCheckbox = e.data.status !== "SUCCESS";
              if (disableCheckbox) {
                setCheckBoxesMode("always");
              } else {
                setCheckBoxesMode("none");
              }
            }
          }}
        >
          <Selection
            mode="multiple" selectAllMode={"page"} showCheckBoxesMode={"always"}
            allowSelectAll={Math.ceil((data.totalCount as number)) > SCHEDULE_LIMIT_API_CALLS ? false : true}
          />
          <Scrolling mode="standard" />
          <LoadPanel enabled={false} />
          <SearchPanel visible />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          {appName === "TP,CS" && <Paging enabled={true} defaultPageSize={40} />}
          <Scrolling columnRenderingMode="virtual" />
          <Toolbar>
            <Item location="after">
              <Button onClick={onClickRetrySite} variant="outlined" disabled={disableRetry}>
                retry
              </Button>
            </Item>
            <Item location="after">
              <FormControl size="medium" variant="outlined" sx={{ width: 400 }}>
                <Select
                  style={{ height: 36, fontSize: "14px" }}
                  defaultValue={dropDown}
                  onChange={handleDropDownChange}
                >
                  {filteredOptions.map(({ name, value }) => (
                    <MenuItem key={value} value={value} style={{ fontSize: "14px" }}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Item>
            <Item widget="dxButton" options={exportButtonOptions} name="alignRight" />
            <Item name="searchPanel" location="after" />
            <Item widget="dxButton" options={refreshButtonOptions} name="alignRight" />
          </Toolbar>

          <Column width="70px" alignment="left" dataField="hqSiteId" caption={"SiteID"} />
          <Column width="70px" alignment="left" dataField="mdmClientId" caption={"MasterID"} />
          <Column width="70px" alignment="left" dataField="mdmClientId" caption={"ClientID"} />
          <Column dataField="hqClientName" caption={"SiteName"} />
          <Column width="170px" dataField="siteCreatedBy" caption={"Site Created by"} />
          <Column
            width="190px"
            cellRender={(e) => cellRenderDate(e.data.siteCreated)}
            dataField="Site Created Date / Time"
            caption={"Site Created Date / Time"}
          />
          <Column cellRender={cellRenderForMessage} width="70px" dataField="status" caption={"Status"} />
          <Column width="170px" dataField="lastModifiedBy" caption={"Last Updated by"} />
          <Column
            width="190px"
            cellRender={(e) => cellRenderDate(e.data.lastModified)}
            dataField="Last Update Date/Time"
            caption={"Last Update Date/Time"}
          />
        </DataGrid>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            margin: "5px 0 0 0",
          }}
        >
          <Pagination
            onChange={(e: any, pageNumber: any) => {
              setpageNumber(pageNumber);
            }}
            count={Math.ceil((data.totalCount as number) / defaultPageSize)}
            variant="outlined"
            shape="rounded"
          />
        </div>
        <DialogBox
          maxWidth={"xs"}
          title={'Alert'}
          btnOpenText={'Ok'}
          isDisplayModal={showAlert}
          handleContinue={handleAlertClose}
          btnCloseText={''}
          icon={<InfoOutlinedIcon sx={{ color: "#009CDE", width: 22, height: 22 }} />}
          dialogContentSx={{ borderTop: "1px solid #e0e0e0" }}
          handleClose={handleAlertClose}
          dialogTitleSx={{ color: '#515356', display: "flex", alignItems: "center", gap: "8px", }}
        >
          <div style={{ margin: "14px 0px" }}>
            <span style={{ fontSize: "16px" }}>
              {getLimitMessage("A maximum of {xx} items can be selected.")}
            </span>
          </div>
        </DialogBox>
      </div>
    </Modal>
  );
};